import { Plus, Trash2, ChevronUp, ChevronDown, Save } from "lucide-react";
import { useCrud } from "./useCrud";
import { ImageField, Field } from "./ImageField";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const RowActions = ({ onSave, onUp, onDown, onDelete }) => (
  <div className="flex items-center gap-2">
    <button onClick={onSave} className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal/90 text-obsidian px-4 py-2 hover:bg-teal"><Save className="h-4 w-4" /> Save</button>
    <button onClick={onUp} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronUp className="h-4 w-4" /></button>
    <button onClick={onDown} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronDown className="h-4 w-4" /></button>
    <button onClick={onDelete} className="ml-auto p-2 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
  </div>
);

export const JobsEditor = () => {
  const { items, patch, setItem, add, save, remove, move } = useCrud("jobs", { title: "New Job", tag: "Trade", slug: "new-job", body: "" });
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-[#8F877C] text-sm">Add and edit the jobs your server offers.</p>
        <button onClick={add} data-testid="job-add-btn" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2.5 hover:bg-bone transition-colors"><Plus className="h-4 w-4" /> Add Job</button>
      </div>
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`job-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title" value={it.title} onChange={(v) => setItem(it.id, { title: v, slug: it.slug || slugify(v) })} />
            <Field label="Tag / Category" value={it.tag} onChange={(v) => patch(it.id, "tag", v)} />
            <Field label="Slug (URL)" value={it.slug} onChange={(v) => patch(it.id, "slug", slugify(v))} />
            <Field label="Pay" value={it.pay} onChange={(v) => patch(it.id, "pay", v)} placeholder="$40 – $150 / shift" />
          </div>
          <Field label="Short description (card)" value={it.body} onChange={(v) => patch(it.id, "body", v)} />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Intro (detail page)</p>
            <textarea value={it.intro || ""} onChange={(e) => patch(it.id, "intro", e.target.value)} rows={2}
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2 text-sm text-bone focus:outline-none focus:border-teal resize-none" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Duties (one per line)</p>
            <textarea value={(it.duties || []).join("\n")} onChange={(e) => patch(it.id, "duties", e.target.value.split("\n").filter(Boolean))} rows={4}
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2 text-sm text-bone focus:outline-none focus:border-teal resize-none" />
          </div>
          <Field label="How to start" value={it.howto} onChange={(v) => patch(it.id, "howto", v)} />
          <Field label="Requirements" value={it.req} onChange={(v) => patch(it.id, "req", v)} />
          <ImageField label="Card image (optional)" value={it.image} onChange={(v) => patch(it.id, "image", v)} />
          <RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

const CLASSES = ["Infraction", "Misdemeanor", "Felony", "Capital"];

export const PenalEditor = () => {
  const { items, patch, add, save, remove, move } = useCrud("penal", { code: "PC-000", charge: "New charge", cls: "Infraction", fine: "$0", time: "—" });
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-[#8F877C] text-sm">Add and edit charges, classes, fines and sentences.</p>
        <button onClick={add} data-testid="penal-add-btn" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2.5 hover:bg-bone transition-colors"><Plus className="h-4 w-4" /> Add Charge</button>
      </div>
      {items.map((it, idx) => (
        <div key={it.id} data-testid={`penal-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Code" value={it.code} onChange={(v) => patch(it.id, "code", v)} />
            <Field label="Charge" value={it.charge} onChange={(v) => patch(it.id, "charge", v)} />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Class</p>
              <select value={it.cls} onChange={(e) => patch(it.id, "cls", e.target.value)}
                className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-sm text-bone focus:outline-none focus:border-teal">
                {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Fine" value={it.fine} onChange={(v) => patch(it.id, "fine", v)} />
            <Field label="Sentence" value={it.time} onChange={(v) => patch(it.id, "time", v)} />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Definition</p>
            <textarea value={it.definition || ""} onChange={(e) => patch(it.id, "definition", e.target.value)} rows={2}
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2 text-sm text-bone focus:outline-none focus:border-teal resize-none" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Penalty note (per-offense specifics)</p>
            <textarea value={it.penalty || ""} onChange={(e) => patch(it.id, "penalty", e.target.value)} rows={2} placeholder="e.g. 1st offense fine only; repeat within 30 days doubles the sentence."
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2 text-sm text-bone focus:outline-none focus:border-teal resize-none" />
          </div>
          <RowActions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};
